
import React, { useState, useEffect } from 'react';
import { getNavigationStats } from '@/utils/navigationMonitor';
import PerformanceDisplay from './PerformanceDisplay';
import DiagnosticsTabs from './tabs/DiagnosticsTabs';
import DiagnosticsFooter from './DiagnosticsFooter';
import { isDiagnosticsEnabled, isDevelopment } from './utils/diagnosticsUtils';
import { useConsoleInterceptor } from './hooks/useConsoleInterceptor';

const DiagnosticsView: React.FC = () => {
  const [showDiagnostics, setShowDiagnostics] = useState(isDiagnosticsEnabled());
  const [stats, setStats] = useState(getNavigationStats());
  const [showToggleButton, setShowToggleButton] = useState(false);
  const [activeTab, setActiveTab] = useState<'navigation' | 'errors' | 'console'>('navigation');
  const { consoleMessages, setConsoleMessages } = useConsoleInterceptor(showDiagnostics);
  
  useEffect(() => {
    // Update diagnostics state based on URL or localStorage
    setShowDiagnostics(isDiagnosticsEnabled());
    
    // Show toggle button in non-production environments
    setShowToggleButton(isDevelopment());
    
    // Update stats periodically
    const statsInterval = setInterval(() => {
      setStats(getNavigationStats());
    }, 1000);
    
    return () => {
      clearInterval(statsInterval);
    };
  }, [showDiagnostics]);
  
  const toggleDiagnostics = () => {
    const newSetting = !showDiagnostics;
    setShowDiagnostics(newSetting);
    localStorage.setItem('diagnostics_enabled', String(newSetting));
    
    // Reload the page to ensure console interception works properly
    if (newSetting) {
      window.location.reload();
    }
  };
  
  return (
    <>
      {showDiagnostics && (
        <div className="fixed bottom-4 left-4 z-50 max-w-md bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 text-xs">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-sm">Diagnostics</h3>
            <div className="flex items-center gap-2">
              <div className="flex text-xs bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                <button
                  onClick={() => window.location.reload()}
                  title="Reload page"
                  className="px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  ↻
                </button>
              </div>
              <button
                onClick={toggleDiagnostics}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Hide
              </button>
            </div>
          </div>
          
          <DiagnosticsTabs 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            stats={stats}
            setStats={setStats}
            consoleMessages={consoleMessages}
            setConsoleMessages={setConsoleMessages}
          />
          
          <DiagnosticsFooter />
        </div>
      )}
      
      {/* Performance metrics display */}
      <PerformanceDisplay isVisible={showDiagnostics} />
      
      {showToggleButton && !showDiagnostics && (
        <button
          onClick={toggleDiagnostics}
          className="fixed bottom-4 left-4 z-50 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 p-2 rounded-full shadow-md"
          title="Show Diagnostics"
        >
          <span className="sr-only">Show Diagnostics</span>
          🛠️
        </button>
      )}
    </>
  );
};

export default DiagnosticsView;
